import { gtcHandleize, gtcGetCookie, gtcSetCookie } from "gtcUtils";
import { gtcModal } from "gtcOpener";

class GTCGeolocator extends gtcModal {

  #shouldOpen = false;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    this.dom = {
      countryItems: this.querySelectorAll('[data-country-code]'),
    };

    this.settings = {
      cookieName: "GTC_GEOLOCATOR",
      currentCountry: this.getAttribute("current-country") || "",
      currentPageType: this.getAttribute("current-page-type") || "",
      translate: this.getAttribute("translate"),
      designMode: this.getAttribute("data-design-mode") || false,
    };

    if (this.settings.designMode) {
      this.open();
    } else {
      if (gtcGetCookie(this.settings.cookieName) === null) {
        this.initGeoLocator();
      }
    }

    super.addListeners();
    this.dom.countryItems.forEach(item => {
      item.addEventListener("click", this.handleRedirect.bind(this));
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    super.removeListeners();

    this.dom.countryItems.forEach(item => {
      item.removeEventListener("click", this.handleRedirect.bind(this));
    });
  }

  async handleRedirect(event) {
    if (!(event.target instanceof HTMLAnchorElement)) {
      return;
    }

    const validRedirects = ["product", "collection", "page"];

    if (validRedirects.includes(this.settings.currentPageType)) {
      event.preventDefault();

      const url = new URL(event.target.href);
      url.pathname = window.location.pathname.slice(1);
      url.search = '';

      let response = null;

      try {
        response = await fetch(`${url.toString().replace('www.', '')}.json`);
      } catch (error) {
        console.error(error);
      }

      if (response && response.status === 200) {
        window.location.href = url.toString();
        return;
      }

      window.location.href = event.target.href;
    } else {
      this.close();
    }
  }

  /**
 * Initializes the geolocator popup.
 * Fetches the user's country and updates the flag image accordingly.
 * Handles the country selection change event.
 * Updates the geolocator state based on the selected country.
 * If the selected country is the main country, it removes the active class and restores the body overflow.
 */
  async initGeoLocator() {
    const country = await this.getCountry();

    if (this.settings.translate) {
      this.translate(country);
    }

    const fallback = Array.from(this.dom.countryItems).find(item => item.getAttribute("data-fallback") !== null);
    const match = Array.from(this.dom.countryItems).find(item => item.getAttribute("data-country-code") === country);

    if (match) {
      if (match.getAttribute("data-country-code") !== this.settings.currentCountry) {
        match.classList.add('is-recommended');
        this.#shouldOpen = true;
      }
    } else if (fallback) {
      fallback.classList.add('is-recommended');
      this.#shouldOpen = true;
    }

    if (this.#shouldOpen) {
      this.open();
    } else {
      this.close();
    }
  }

  /**
   * Fetches the country based on the user's IP address using the GeoJS API.
   * Returns the country name in a handleized format.
   * @returns {Promise<string>} The handleized country name.
   */
  async getCountry() {
    const response = await fetch("https://get.geojs.io/v1/ip/country.json");
    const body = await response.json();
    return gtcHandleize(body.country);
  }

  translate(country) {
    let lang = navigator.language || navigator.userLanguage;

    // if geojs lang
    if (this.settings.translate === "geojs") {
      lang = country;
    }

    const regionNamesByBrowser = new Intl.DisplayNames([lang, 'en'], { type: 'region' });
    const items = Array.from(this.dom.countryItems);

    items.forEach((item) => {
      const countryCode = item.getAttribute("data-country-code");
      const countryName = regionNamesByBrowser.of(countryCode.toUpperCase());
      const name = item.querySelector("[data-country-name]");

      if (!name) {
        return;
      }

      name.textContent = countryName;
    });
  }

  close() {
    super.close();
    gtcSetCookie(this.settings.cookieName, "true", 30);
  }
}

if (!customElements.get("gtc-geolocator")) {
  customElements.define("gtc-geolocator", GTCGeolocator);
}
